import {useCallback, useEffect, useRef} from 'react';

import {joinPodUrl} from './joinPodUrl';
import {normalizeLoxoPodUrl} from './podUrl';
import {useRoamingSettings} from './RoamingSettingsContext';

const ACCESS_TOKEN_EXPIRED = 'access_token_expired';

export type PodRequestFn = (
  path: string,
  init?: RequestInit
) => Promise<Response>;
export type PodPostRequestFn = (
  path: string,
  body: unknown
) => Promise<Response>;

type AuthRequestState = {
  jwt: string | undefined;
  podUrl: string | undefined;
  refreshToken: string | undefined;
};

type AuthToken = {
  access_token: string;
  refresh_token: string;
};

const refreshPromises = new Map<string, Promise<AuthToken>>();

const postRefresh = async (
  podUrl: string,
  refreshToken: string
): Promise<AuthToken> => {
  const response = await fetch(joinPodUrl(podUrl, '/auth/refresh_token'), {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({refresh_token: refreshToken}),
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok || !body || typeof body !== 'object') {
    throw new Error('Could not refresh session.');
  }
  const access_token = (body as {access_token?: unknown}).access_token;
  const refresh_token = (body as {refresh_token?: unknown}).refresh_token;
  if (typeof access_token !== 'string' || typeof refresh_token !== 'string') {
    throw new Error('Could not refresh session.');
  }
  return {access_token, refresh_token};
};

const postRefreshOnce = (
  podUrl: string,
  refreshToken: string
): Promise<AuthToken> => {
  const key = `${podUrl}|${refreshToken}`;
  const existing = refreshPromises.get(key);
  if (existing) {
    return existing;
  }

  const promise = postRefresh(podUrl, refreshToken).finally(() => {
    refreshPromises.delete(key);
  });
  refreshPromises.set(key, promise);
  return promise;
};

export const usePodRequest = (): {
  request: PodRequestFn;
  postRequest: PodPostRequestFn;
} => {
  const {
    podUrl,
    jwt,
    refreshToken,
    setJwt,
    setRefreshToken,
    clearJwtAndRefreshTokens,
    refreshFromParent,
  } = useRoamingSettings();
  const authRequestStateRef = useRef<AuthRequestState>({
    jwt,
    podUrl,
    refreshToken,
  });

  useEffect(() => {
    authRequestStateRef.current = {jwt, podUrl, refreshToken};
  }, [jwt, podUrl, refreshToken]);

  const persistTokens = useCallback(
    async (baseUrl: string, tokens: AuthToken): Promise<void> => {
      authRequestStateRef.current = {
        ...authRequestStateRef.current,
        podUrl: baseUrl,
        jwt: tokens.access_token,
        refreshToken: tokens.refresh_token,
      };
      await setJwt(tokens.access_token);
      await setRefreshToken(tokens.refresh_token);
    },
    [setJwt, setRefreshToken]
  );

  const refreshSessionWithToken = useCallback(
    async (baseUrl: string, currentRefreshToken: string) => {
      const refreshTokenForRequest = currentRefreshToken;
      let tokens: AuthToken;

      try {
        tokens = await postRefreshOnce(baseUrl, refreshTokenForRequest);
      } catch (error) {
        if (!refreshFromParent) {
          throw error;
        }

        const latestSettings = await refreshFromParent().catch(() => null);
        const latestBaseUrl = latestSettings?.podUrl
          ? normalizeLoxoPodUrl(latestSettings.podUrl)
          : undefined;
        const latestRefreshToken = latestSettings?.refreshToken;

        if (
          latestBaseUrl !== baseUrl ||
          !latestRefreshToken ||
          latestRefreshToken === refreshTokenForRequest
        ) {
          throw error;
        }

        tokens = await postRefreshOnce(baseUrl, latestRefreshToken);
      }

      await persistTokens(baseUrl, tokens);
      return tokens;
    },
    [persistTokens, refreshFromParent]
  );

  const resolveAuthFromRoaming = useCallback(async () => {
    let {
      podUrl: rawPodUrl,
      jwt: accessToken,
      refreshToken: currentRefreshToken,
    } = authRequestStateRef.current;
    rawPodUrl = rawPodUrl?.trim();

    if (
      refreshFromParent &&
      (!rawPodUrl || !accessToken || !currentRefreshToken)
    ) {
      const settings = await refreshFromParent();
      const authSettings = settings as typeof settings & {
        jwt?: string;
      };
      rawPodUrl = authSettings.podUrl?.trim();
      accessToken = authSettings.jwt;
      currentRefreshToken = authSettings.refreshToken;
      authRequestStateRef.current = {
        podUrl: authSettings.podUrl,
        jwt: authSettings.jwt,
        refreshToken: authSettings.refreshToken,
      };
    }

    const baseUrl = rawPodUrl ? normalizeLoxoPodUrl(rawPodUrl) : undefined;
    return {baseUrl, accessToken, currentRefreshToken};
  }, [refreshFromParent]);

  const request = useCallback(
    async (path: string, init?: RequestInit) => {
      const {
        baseUrl,
        accessToken,
        currentRefreshToken: initialRefreshToken,
      } = await resolveAuthFromRoaming();
      let currentAccessToken = accessToken;
      let currentRefreshToken = initialRefreshToken;
      if (!baseUrl) {
        throw new Error('podUrl is not set.');
      }
      if (!currentRefreshToken) {
        throw new Error('Refresh token is not set.');
      }

      const url = joinPodUrl(baseUrl, path);

      const send = (accessToken: string) => {
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${accessToken}`);

        return fetch(url, {
          ...init,
          headers,
        });
      };

      const refreshSession = async () => {
        if (!currentRefreshToken) {
          throw new Error('Refresh token is not set.');
        }

        const tokens = await refreshSessionWithToken(
          baseUrl,
          currentRefreshToken
        );
        currentAccessToken = tokens.access_token;
        currentRefreshToken = tokens.refresh_token;
        return tokens.access_token;
      };

      if (!currentAccessToken) {
        try {
          currentAccessToken = await refreshSession();
        } catch {
          await clearJwtAndRefreshTokens();
          throw new Error('Session expired. Sign in again.');
        }
      }

      const response = await send(currentAccessToken);

      if (response.status !== 401) {
        return response;
      }

      const unauthorized = await response
        .clone()
        .json()
        .catch(() => null);
      if (
        !unauthorized ||
        typeof unauthorized !== 'object' ||
        (unauthorized as {error_code?: string}).error_code !==
          ACCESS_TOKEN_EXPIRED
      ) {
        return response;
      }

      let refreshedAccessToken: string;
      try {
        refreshedAccessToken = await refreshSession();
      } catch {
        await clearJwtAndRefreshTokens();
        throw new Error('Session expired. Sign in again.');
      }
      return send(refreshedAccessToken);
    },
    [clearJwtAndRefreshTokens, refreshSessionWithToken, resolveAuthFromRoaming]
  );

  const postRequest = useCallback<PodPostRequestFn>(
    async (path: string, body: unknown) => {
      const {
        baseUrl,
        accessToken,
        currentRefreshToken: initialRefreshToken,
      } = await resolveAuthFromRoaming();
      let currentAccessToken = accessToken;
      let currentRefreshToken = initialRefreshToken;
      if (!baseUrl) {
        throw new Error('podUrl is not set.');
      }
      if (!currentRefreshToken) {
        throw new Error('Refresh token is not set.');
      }

      const url = joinPodUrl(baseUrl, path);

      const send = (token: string) =>
        fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

      const refreshSession = async () => {
        if (!currentRefreshToken) {
          throw new Error('Refresh token is not set.');
        }

        const tokens = await refreshSessionWithToken(
          baseUrl,
          currentRefreshToken
        );
        currentAccessToken = tokens.access_token;
        currentRefreshToken = tokens.refresh_token;
        return tokens.access_token;
      };

      if (!currentAccessToken) {
        try {
          currentAccessToken = await refreshSession();
        } catch {
          await clearJwtAndRefreshTokens();
          throw new Error('Session expired. Sign in again.');
        }
      }

      const response = await send(currentAccessToken);

      if (response.status !== 401) {
        return response;
      }

      const unauthorized = await response
        .clone()
        .json()
        .catch(() => null);
      if (
        !unauthorized ||
        typeof unauthorized !== 'object' ||
        (unauthorized as {error_code?: string}).error_code !==
          ACCESS_TOKEN_EXPIRED
      ) {
        return response;
      }

      try {
        return send(await refreshSession());
      } catch {
        await clearJwtAndRefreshTokens();
        throw new Error('Session expired. Sign in again.');
      }
    },
    [clearJwtAndRefreshTokens, refreshSessionWithToken, resolveAuthFromRoaming]
  );

  return {request, postRequest};
};
